import { getAttachmentDownloadUrl } from '../services/appsync-client'

/**
 * Resolves a raw attachment URL (S3 key/URL or external preview URL) to a
 * displayable download URL, caching the in-flight/resolved promise per raw URL.
 *
 * The cache dedupes the presigned-URL lookups that the image gallery and the
 * full-screen lightbox both need, so opening / paging through a thread's media
 * never fans out into a storm of `getAttachmentDownloadUrl` calls (which has
 * caused Lambda 429s in the past).
 *
 * Presigned S3 GET URLs EXPIRE (default 1h). A chat thread left open in a
 * long-lived native webview outlives that easily, so entries carry the presign
 * expiry (from the resolver's `expiresAt`, minus a safety margin) and are
 * re-resolved once stale — otherwise every image in the thread quietly breaks.
 */
type CacheEntry = {
  promise: Promise<string>
  /** Epoch ms after which the resolved URL must not be reused. */
  expiresAtMs: number
}

const cache = new Map<string, CacheEntry>()

/** Re-resolve this long before the presigned URL actually expires. */
const EXPIRY_SAFETY_MARGIN_MS = 60_000
/** Fallback TTL when the resolver doesn't return `expiresAt`. */
const DEFAULT_TTL_MS = 10 * 60_000

export function resolveAttachmentUrlCached(rawUrl: string): Promise<string> {
  const existing = cache.get(rawUrl)
  if (existing && Date.now() < existing.expiresAtMs) return existing.promise

  // Block reuse-checks while in flight; the real expiry is set on resolve.
  const entry: CacheEntry = {
    expiresAtMs: Number.MAX_SAFE_INTEGER,
    promise: Promise.resolve(''),
  }
  entry.promise = (async () => {
    if (!rawUrl.includes('amazonaws.com')) {
      // Stable external URL (e.g. GIF CDN) — never expires.
      return rawUrl
    }
    const { downloadUrl, expiresAt } = await getAttachmentDownloadUrl(rawUrl)
    const parsed = expiresAt ? Date.parse(expiresAt) : Number.NaN
    entry.expiresAtMs = Number.isFinite(parsed)
      ? parsed - EXPIRY_SAFETY_MARGIN_MS
      : Date.now() + DEFAULT_TTL_MS
    return downloadUrl
  })().catch((err) => {
    // Drop failed lookups so a later retry can re-resolve instead of caching the rejection.
    cache.delete(rawUrl)
    throw err
  })

  cache.set(rawUrl, entry)
  return entry.promise
}

/**
 * Drop a cached resolution (e.g. after an `<img onError>`: the presigned URL
 * may have expired server-side before our conservative TTL lapsed).
 */
export function invalidateAttachmentUrl(rawUrl: string): void {
  cache.delete(rawUrl)
}
